import { PersistenceLevel, Store } from '@/core/flux.service';
import {
  defaultLayoutDefinition,
  ImpactReportCategory,
  ImpactReportConfiguration,
  ImpactReportDashboardContent,
  PredefinedDateRangeOptions,
} from '@/impactReports/impactReport.types';
import _ from 'lodash';
import moment from 'moment';
import { ColumnDefinitionOutputV1 } from '@/sdk';

interface ImpactReportState {
  tableId: string | undefined;
  forceTableReload: boolean;
  categories: ImpactReportCategory[] | undefined;
  startDate: string; //ISOString;
  endDate: string; //ISOString;
  layoutDefinition: ImpactReportDashboardContent[];
  globalLayout: ImpactReportDashboardContent[];
  aggregationColumn: string | undefined;
  biggestImpact: number;
  useCaseCount: number;
  overallImpact: number;
  filteredImpact: number;
  displayEditModal: boolean;
  updateNominations: boolean;
  definedTimeRange: string | undefined;
  customKpi: ColumnDefinitionOutputV1[];
  largestKPIImpacts: Record<string, any>;
  filteredTotals: Record<string, any>;
  absoluteTotals: Record<string, any>;
}

export class ImpactReportStore extends Store {
  static readonly storeName = 'sqImpactReportStore';
  persistenceLevel: PersistenceLevel = 'WORKBENCH';

  get tableId(): string | undefined {
    return this.state.get('tableId');
  }

  get startDate(): string | undefined {
    return this.state.get('startDate');
  }

  get definedTimeRange(): PredefinedDateRangeOptions | undefined {
    return this.state.get('definedTimeRange');
  }

  get endDate(): string | undefined {
    return this.state.get('endDate');
  }

  get categories(): ImpactReportCategory[] | undefined {
    return this.state.get('categories');
  }

  get layoutDefinition(): ImpactReportDashboardContent[] {
    return this.state.get('layoutDefinition');
  }

  get forceTableReload(): boolean {
    return this.state.get('forceTableReload');
  }

  get aggregationColumn(): string | undefined {
    return this.state.get('aggregationColumn');
  }

  get biggestImpact(): number {
    return this.state.get('biggestImpact');
  }

  get useCaseCount(): number {
    return this.state.get('useCaseCount');
  }

  get overallImpact(): number {
    return this.state.get('overallImpact');
  }

  get filteredImpact(): number {
    return this.state.get('filteredImpact');
  }

  get displayEditModal(): boolean {
    return this.state.get('displayEditModal');
  }

  get showAssetPath(): boolean {
    return this.state.get('showAssetPath');
  }

  get showSite(): boolean {
    return this.state.get('showSite');
  }

  get showImpact(): boolean {
    return this.state.get('showImpact');
  }

  get globalLayout(): ImpactReportDashboardContent[] {
    return this.state.get('globalLayout');
  }

  get updateNominations(): boolean {
    return this.state.get('updateNominations');
  }

  get impactReportConfiguration(): ImpactReportConfiguration {
    return {
      showAssetPath: this.showAssetPath,
      showSite: this.showSite,
      globalLayout: this.globalLayout,
      showImpact: this.showImpact,
    };
  }
  get kpiColumns(): ColumnDefinitionOutputV1[] {
    return this.state.get('kpiColumns');
  }

  get absoluteTotals(): Record<string, any> {
    return this.state.get('absoluteTotals');
  }

  get filteredTotals(): Record<string, any> {
    return this.state.get('filteredTotals');
  }

  get largestKPIImpacts(): Record<string, any> {
    return this.state.get('largestKPIImpacts');
  }

  initialize() {
    const now = moment.utc();

    this.state = this.immutable({
      tableId: undefined,
      categories: undefined,
      startDate: now.subtract(1, 'year').toISOString(),
      endDate: now.toISOString(),
      forceTableReload: false,
      layoutDefinition: defaultLayoutDefinition,
      globalLayout: undefined,
      aggregationColumn: undefined,
      biggestImpact: 0,
      useCaseCount: 0,
      overallImpact: 0,
      filteredImpact: 0,
      displayEditModal: false,
      showAssetPath: true,
      showSite: true,
      updateNominations: false, // to make sure they are fetched initially
      definedTimeRange: 'pastYear',
      showImpact: true,
      kpiColumns: [],
      absoluteTotals: [],
      filteredTotals: [],
      largestKPIImpacts: [],
    });
  }

  dehydrate() {
    return _.pick(this.state.serialize(), ['layoutDefinition', 'startDate', 'endDate', 'definedTimeRange']);
  }

  rehydrate(dehydratedState: ImpactReportState) {
    this.state.merge(dehydratedState);
  }

  protected readonly handlers = {
    IMPACT_REPORT_SET_TABLE_ID: (payload: { tableId: string }) => {
      this.state.set('tableId', payload.tableId);
    },
    IMPACT_REPORT_SET_CATEGORIES: (payload: { categories: ImpactReportCategory[] }) => {
      this.state.set('categories', payload.categories);
    },
    IMPACT_REPORT_SET_START_DATE: (payload: { startDate: string }) => {
      this.state.set('startDate', payload.startDate);
    },
    IMPACT_REPORT_SET_END_DATE: (payload: { endDate: string }) => {
      this.state.set('endDate', payload.endDate);
    },
    IMPACT_REPORT_SET_LAYOUT_DEFINITION: (payload: { layoutDefinition: ImpactReportDashboardContent[] }) => {
      this.state.set('layoutDefinition', payload.layoutDefinition);
    },
    IMPACT_REPORT_REMOVE_CONTENT: (payload: { i: string }) => {
      this.state.set(
        'layoutDefinition',
        this.layoutDefinition.filter((content: { i: string }) => content.i !== payload.i),
      );
    },
    IMPACT_REPORT_SET_FORCE_TABLE_RELOAD: (payload: { forceTableReload: boolean }) => {
      this.state.set('forceTableReload', payload.forceTableReload);
    },
    IMPACT_REPORT_SET_AGGREGATION_COLUMN: (payload: { aggregationColumn: string }) => {
      this.state.set('aggregationColumn', payload.aggregationColumn);
    },
    IMPACT_REPORT_SET_BIGGEST_IMPACT: (payload: { biggestImpact: number }) => {
      this.state.set('biggestImpact', payload.biggestImpact);
    },
    IMPACT_REPORT_SET_USE_CASE_COUNT: (payload: { useCaseCount: number }) => {
      this.state.set('useCaseCount', payload.useCaseCount);
    },
    IMPACT_REPORT_SET_OVERALL_IMPACT: (payload: { overallImpact: number }) => {
      this.state.set('overallImpact', payload.overallImpact);
    },
    IMPACT_REPORT_SET_FILTERED_IMPACT: (payload: { filteredImpact: number }) => {
      this.state.set('filteredImpact', payload.filteredImpact);
    },
    IMPACT_REPORT_SET_DISPLAY_EDIT_MODAL: (payload: { displayEditModal: boolean }) => {
      this.state.set('displayEditModal', payload.displayEditModal);
    },

    IMPACT_REPORT_SET_SHOW_ASSET_PATH: (payload: { showAssetPath: boolean }) => {
      this.state.set('showAssetPath', payload.showAssetPath);
    },

    IMPACT_REPORT_SET_SHOW_SITE: (payload: { showSite: boolean }) => {
      this.state.set('showSite', payload.showSite);
    },

    IMPACT_REPORT_SET_SHOW_IMPACT: (payload: { showImpact: boolean }) => {
      this.state.set('showImpact', payload.showImpact);
    },

    IMPACT_REPORT_SET_GLOBAL_LAYOUT: (payload: { globalLayout: ImpactReportDashboardContent[] }) => {
      this.state.set('globalLayout', payload.globalLayout);
    },

    IMPACT_REPORT_SET_UPDATE_NOMINATIONS: (payload: { updateNominations: boolean }) => {
      this.state.set('updateNominations', payload.updateNominations);
    },

    IMPACT_REPORT_SET_DEFINED_TIME_RANGE: (payload: { definedTimeRange: PredefinedDateRangeOptions }) => {
      this.state.set('definedTimeRange', payload.definedTimeRange);
    },
    IMPACT_REPORT_SET_KPI_COLUMNS: (payload: { kpiColumns: ColumnDefinitionOutputV1[] }) => {
      this.state.set('kpiColumns', payload.kpiColumns);
    },

    IMPACT_REPORT_SET_ABSOLUTE_TOTALS: (payload: { absoluteTotals: Record<string, any> }) => {
      this.state.set('absoluteTotals', payload.absoluteTotals);
    },

    IMPACT_REPORT_SET_FILTERED_TOTALS: (payload: { filteredTotals: Record<string, any> }) => {
      this.state.set('filteredTotals', payload.filteredTotals);
    },

    IMPACT_REPORT_SET_LARGEST_KPI_IMPACTS: (payload: { largestKPIImpacts: Record<string, any> }) => {
      this.state.set('largestKPIImpacts', payload.largestKPIImpacts);
    },
  };
}
